/**
 * 보고서에 넣을 막대 그래프를 결과의 모양으로 고른다.
 *
 * 무엇을 그릴지 모델에게 묻지 않는다 — 계산 결과의 요약과 표는 이미 숫자다. 그래프가
 * 없어 「그래프로 이쁘게」가 표만 든 PDF 여러 쪽이 됐다(실제 테넌트, 2026-10-23).
 */
import type { ReportChart, TableData } from './files';
import { calendarDay, dateColumn, shortDate, statusColumn, statusName } from './reportData';

/** 그림 후보 수. 표와 겹치는 그림을 뺀 뒤 부르는 쪽이 둘로 자른다 — 먼저 자르면 남길 그림이 잘려 나갔다. */
const MAX_CHARTS = 3;
const TOP = 10;
const STACK_TOP = 7;
/** 여러 달 판은 〈표 2〉가 묶음별로 길어(10줄) 다섯 곳까지. 일곱 곳이면 1,180px로 한 장을 14% 넘었다(2026-10-25 11:01). */
const MULTI_STACK_TOP = 5;
const MAX_SUMMARY_KEYS = 12;
/** 이런 이름의 계열은 사람이 먼저 봐야 한다 — 강조색을 칠한다. */
const ATTENTION = /미실시|미처리|미수|미결|미완료|지연|초과|누락|부적합|미납|미입고/;

function numberOf(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  if (!/^-?[\d,]+(?:\.\d+)?$/.test(text)) return undefined;
  const parsed = Number(text.replace(/,/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function plainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** 둘 이상을 골라 더해 target 이 되는가. 칸은 열둘을 넘지 않는다. */
function subsetSum(values: number[], target: number): boolean {
  const count = values.length;
  for (let mask = 1; mask < 1 << count; mask += 1) {
    let picked = 0;
    let sum = 0;
    for (let index = 0; index < count; index += 1) {
      if (mask & (1 << index)) {
        picked += 1;
        sum += values[index] as number;
      }
    }
    if (picked >= 2 && sum === target) return true;
  }
  return false;
}

/** 한 겹(이름→숫자)이면 단일 계열, 두 겹(범주→계열→숫자)이면 묶음 막대. */
function summaryChart(summary: unknown): ReportChart | undefined {
  if (!plainObject(summary)) return undefined;
  const entries = Object.entries(summary);
  let flat = entries.filter(([key, value]) => measurable(key, value));
  // 「전체」가 나머지의 합이면 칸끼리 겹친다 — 빼야 구성(완료·미실시)이 된다(실제 테넌트, 2026-10-23).
  // 다른 칸 몇 개의 합인 칸은 묶음 값이다 — 전체(이름이 「점검 건수」여도), 계획 = 미진행 + 지연.
  // 칸끼리 겹치면 막대가 같은 것을 두 번 센다(실제 테넌트, 2026-10-23).
  if (flat.length >= 3) {
    const numbers = flat.map(([, value]) => numberOf(value) as number);
    const largest = Math.max(...numbers);
    const total = flat.some((_, at) => numbers[at] === largest && subsetSum(numbers.filter((_, index) => index !== at), largest))
      ? largest : undefined;
    flat = flat.filter(([key], at) => {
      const value = numbers[at] as number;
      const aggregateName = /전체|합계|총|소계|계획|미완료|미실시|미처리/.test(key);
      return !((value === largest || aggregateName) && subsetSum(numbers.filter((_, index) => index !== at), value));
    });
    // 전체를 뺀 나머지가 전체보다 크면 한 칸이 다른 칸의 일부다 — 점검일이 지난 미완료 52 는 계획 상태 432 의
    // 일부였다(실제 테넌트, 2026-10-23). 그 한 칸을 빼면 전체와 맞는 칸을 뺀다.
    if (total !== undefined) {
      const rest = flat.map(([, value]) => numberOf(value) as number);
      const sum = rest.reduce((all, value) => all + value, 0);
      const part = sum > total ? rest.findIndex((value) => sum - value === total) : -1;
      if (part >= 0) flat = flat.filter((_, index) => index !== part);
    }
  }
  if (flat.length >= 2 && flat.length <= MAX_SUMMARY_KEYS) {
    if (!spreadOk(flat.map(([, value]) => numberOf(value) as number))) return undefined;
    // 「계획(미실시)」처럼 상태 코드에 풀이를 붙인 이름은 풀이만 — 보고 문서에 상태 코드를 쓰지 않는다.
    // 「완료 건수」·「계획 상태 건수」의 꼬리는 떼고 본다 — 칸 이름은 「완료」·「미실시」.
    const named = flat.map(([key]) => key.replace(/^계획\s*\(([^)]+)\)$/, '$1')
      .replace(/\s*(?:상태)?\s*(?:건수|건)$/, '').trim());
    // 「계획」은 「완료」 곁에서 아직 안 한 것 — 보고 문서의 말로 「미실시」.
    const categories = named.indexOf('완료') >= 0 ? named.map((key) => (key === '계획' ? '미실시' : key)) : named;
    return {
      title: '주요 수치',
      categories,
      series: [{ name: '값', values: flat.map(([, value]) => numberOf(value) as number) }],
      highlight: attentionOf(categories),
    };
  }
  // 두 겹 묶음이 한 칸 안에 싸여 있으면(「월별 진행 현황」: 달 → 완료·계획) 그것을 꺼내 그린다 —
  // 바깥 칸끼리 묶어 「202609·202610」 계열로 뒤집혀 그렸다(실제 테넌트, 2026-10-24).
  const wrapped = entries.find(([, value]) => plainObject(value) && Object.values(value).length >= 2
    && Object.values(value).every(plainObject));
  if (wrapped) return summaryChart(wrapped[1]);
  const nested = entries.filter(([, value]) => plainObject(value));
  if (nested.length < 2 || nested.length > MAX_SUMMARY_KEYS) return undefined;
  const names: string[] = [];
  nested.forEach(([, value]) => Object.entries(value as Record<string, unknown>).forEach(([key, inner]) => {
    if (measurable(key, inner) && names.indexOf(key) < 0) names.push(key);
  }));
  if (!names.length || names.length > 6) return undefined;
  const valueOf = (name: string, value: unknown) => numberOf((value as Record<string, unknown>)[name]) ?? 0;
  // 달마다 다른 계열의 합인 계열(「전체 건수」)은 뺀다 — 완료·계획과 겹쳐 그렸다(실제 테넌트, 2026-10-23).
  const totals = names.filter((name) => names.length >= 3 && nested.every(([, value]) => valueOf(name, value)
    === names.filter((other) => other !== name).reduce((sum, other) => sum + valueOf(other, value), 0)));
  const kept = names.filter((name) => totals.indexOf(name) < 0);
  // 계열 이름은 꼬리(건수·상태)를 떼고, 「완료」 곁의 「계획」은 「미실시」.
  const bare = kept.map((name) => name.replace(/\s*(?:상태)?\s*(?:건수|건)$/, '').trim());
  const shownNames = bare.indexOf('완료') >= 0 ? bare.map((name) => (name === '계획' ? '미실시' : name)) : bare;
  const all = nested.reduce<number[]>((found, [, value]) => found.concat(kept
    .map((one) => numberOf((value as Record<string, unknown>)[one]))
    .filter((one): one is number => one !== undefined)), []);
  if (!spreadOk(all)) return undefined;
  return {
    title: shownNames.join('·'),
    // 달 범주는 '26.9월 로 — 「202609」가 그대로 찍혔다.
    categories: nested.map(([key]) => shortDate(key)),
    series: kept.map((name, index) => ({
      name: shownNames[index] ?? name,
      // 그 달에 없는 계열은 0 이 아니라 빈 값이다 — 「0건」 막대로 그렸다(검토, 2026-10-23).
      values: nested.map(([, value]) => numberOf((value as Record<string, unknown>)[name]) ?? null),
    })),
    highlight: attentionOf(shownNames),
  };
}

function filled(cell: unknown): boolean {
  return cell !== null && cell !== undefined && String(cell).trim() !== '';
}

/** 가짓수가 이만큼 이하이면 범주(월·결과), 넘으면 이름(현장·담당자)이다. */
const FEW = 12;
/** 날짜·코드·번호는 숫자처럼 보여도 더할 값이 아니다 — 점검일자 20260803 을 더해 그렸다(2026-10-23). */
const NOT_MEASURE = /율|%|비율|비중|일자|날짜|일시|년월|기준월|^월$|^년$|연도|년도|코드|번호|순번|^no\.?$|id$/i;
/** 하루하루의 날짜. 범주로 늘어놓으면 막대가 여든 개가 된다 — 한 장이 깨진다(검토, 2026-10-23). */
const FULL_DATE = /^(?:19|20)\d{2}-?(?:0[1-9]|1[0-2])-?\d{2}$/;
/** 202610·20261023 처럼 기간을 적은 값. 크기가 아니다. */
const PERIOD_VALUE = /^(?:19|20)\d{4}(?:\d{2})?$/;
/** 한 축에 둘 값들의 크기 차. 넘으면 작은 막대가 안 보인다 — 건수와 금액을 한 그래프에 섞었다. */
const MAX_SPREAD = 1000;

function spreadOk(values: number[]): boolean {
  const positive = values.filter((value) => value > 0);
  return positive.length < 2 || Math.max(...positive) / Math.min(...positive) < MAX_SPREAD;
}

function measurable(key: string, value: unknown): boolean {
  const number = numberOf(value);
  return number !== undefined && !NOT_MEASURE.test(key) && !PERIOD_VALUE.test(String(number));
}
/** 이름부터 코드인 열 — 결과·상태·구분·여부·유형·등급. 숫자로 와도 크기가 아니다. */
const CODE_NAME = /결과|상태|구분|여부|유형|등급|코드/;
/** 이름부터 크기인 열 — 값이 한 자리 수뿐이어도 더한다. */
const MEASURE_NAME = /건수|수량|금액|횟수|일수|인원|[수액량]$/;
const NOT_CATEGORY = /일자|날짜|일시|코드|번호|^no$|id$/i;
const DATE_LIKE = /^(?:19|20)\d{2}-?(?:0[1-9]|1[0-2])(?:-?\d{2})?$/;

function attentionOf(names: string[]): string | undefined {
  const marked = names.find((name) => ATTENTION.test(name));
  if (marked) return marked;
  // 「계획」은 「완료」 곁에 있을 때만 아직 안 한 것이다. 「완료 건수」·「계획 건수」처럼 꼬리가 붙어도 같다.
  const bare = (name: string) => name.replace(/\s*(?:건수|건)$/, '').trim();
  return names.some((name) => bare(name) === '완료') ? names.find((name) => bare(name) === '계획') : undefined;
}

/** 달·날짜 같은 범주는 차례대로, 나머지는 많은 순으로. */
function ordered(counts: Map<string, number>): string[] {
  const keys = Array.from(counts.keys());
  if (keys.every((key) => DATE_LIKE.test(key) || /^\d{1,2}월$/.test(key))) {
    return keys.sort((left, right) => (parseInt(left.replace(/\D/g, ''), 10) - parseInt(right.replace(/\D/g, ''), 10)));
  }
  return keys.sort((left, right) => (counts.get(right) ?? 0) - (counts.get(left) ?? 0));
}

/**
 * 상태 열(완료·미실시)이 있는 표. 그릴 것은 구성(처리 상태)과 **주의 상태의** 순위뿐이다 —
 * 현장별 전체 건수는 규모 순위라 보고에 뜻이 없다(국가직 팀장 검토, 2026-10-23). 호기 번호 같은 숫자 글자를
 * 더해 「현장명별 호기명 상위 10」을 그린 적도 있다.
 */
function statusCharts(table: TableData, basis?: Date, stackTop = STACK_TOP, lateFirst = false): ReportChart[] | undefined {
  const status = statusColumn(table);
  if (!status) return undefined;
  const charts: ReportChart[] = [];
  const counts = new Map<string, number>();
  table.rows.forEach((row) => {
    const value = statusName(status, row[status.index]);
    if (value) counts.set(value, (counts.get(value) ?? 0) + 1);
  });
  const names = Array.from(counts.keys())
    .sort((left, right) => (left === status.attention ? 1 : right === status.attention ? -1 : 0));
  if (names.length >= 2 && names.length <= 3) {
    charts.push({
      title: '처리 상태', unit: '건', categories: names,
      series: [{ name: '건수', values: names.map((one) => counts.get(one) ?? 0) }], highlight: status.attention,
    });
  }
  // 주의 상태 건을 이름 열(가짓수가 많은 글자 열)로 센다.
  const flagged = table.rows.filter((row) => statusName(status, row[status.index]) === status.attention);
  const labels = table.columns.map((_, index) => index).filter((index) => {
    if (index === status.index) return false;
    const cells = table.rows.map((row) => row[index]).filter(filled).map((cell) => String(cell).trim());
    return cells.length > 0 && !cells.every((cell) => /^[\d,.\-]+$/.test(cell) || FULL_DATE.test(cell))
      && new Set(cells).size >= 2;
  });
  // 현장·거래처처럼 무엇인지 가리키는 열을 먼저 — 호기명이 현장명보다 앞서 골라졌다(실제 테넌트, 2026-10-23).
  const label = labels.find((index) => /현장|거래처|고객|사업장|부서|업체|매장|지점/.test(table.columns[index] ?? ''))
    ?? labels[0] ?? -1;
  if (label >= 0 && flagged.length) {
    const totals = new Map<string, number>();
    flagged.forEach((row) => {
      if (!filled(row[label])) return;
      const key = String(row[label]).trim();
      totals.set(key, (totals.get(key) ?? 0) + 1);
    });
    // 날짜 열과 기준일이 있으면 경과·예정으로 나눈 누적 막대. 문제 현장(경과)과 정상 현장(예정)을
    // 같은 막대로 그려 판단을 흐렸다(국가직 팀장 2차 검토, 2026-10-23).
    // 날짜 칸과 날짜는 〈표 2〉와 같은 잣대로 센다. 달력에 없는 날(20260931)을 그림만 경과로 세어 〈표 2〉 2건, 그림 3건이었다(코드 리뷰).
    const dateIndex = dateColumn(table);
    const today = basis ? basis.getFullYear() * 10000 + (basis.getMonth() + 1) * 100 + basis.getDate() : 0;
    const written = (row: unknown[]) => String(row[dateIndex] ?? '').trim();
    const broken = (row: unknown[]) => !!written(row) && !calendarDay(written(row));
    const late = (row: unknown[]) => {
      const day = calendarDay(written(row));
      return day > 0 && day < today;
    };
    const count = (key: string, wanted: boolean) => flagged
      .filter((row) => String(row[label] ?? '').trim() === key && !broken(row) && late(row) === wanted).length;
    // 줄여 그리는 판(메모 확인 줄이 든 판)은 예정일 경과가 많은 현장부터 고른다. 네 곳으로 줄이니 세 곳이 예정일 이내
    // 막대였다(13차 검토). 경과가 있는 현장이 둘 이상일 때만 그렇게 하고, 아니면 지금처럼 미실시 건수 순이다.
    const overdue = lateFirst && basis && dateIndex >= 0
      ? Array.from(totals.keys()).map((key) => [key, count(key, true)] as [string, number]).filter(([, value]) => value > 0)
        .sort((left, right) => right[1] - left[1] || (totals.get(right[0]) ?? 0) - (totals.get(left[0]) ?? 0))
      : [];
    const ranked = overdue.length >= 2 ? overdue : Array.from(totals.entries()).sort((left, right) => right[1] - left[1]);
    // 경과·예정 순위는 일곱 곳까지 — 경과 표(일곱 곳)와 맞추고 「한 장」에 들게(2026-10-23, 2쪽이 됐다).
    // 끝이 동점이면 함께 싣는다 — 7위 8건이 둘인데 하나만 실렸다(4차 검토). 셋까지만 더 싣는다.
    let limit = basis ? stackTop : TOP;
    while (limit < ranked.length && limit < (basis ? stackTop : TOP) + 3
      && ranked[limit]?.[1] === ranked[limit - 1]?.[1]) limit += 1;
    const top = ranked.slice(0, limit);
    if (basis && dateIndex >= 0 && top.length >= 2) {
      // 0뿐인 계열은 그리지 않는다 — 경과만 담긴 목록에 「예정」 범례가 나와 예정이 0건으로 읽혔다(3차 검토).
      const series = [
        { name: '예정일 경과', values: top.map(([key]) => count(key, true)) },
        { name: '예정', values: top.map(([key]) => count(key, false)) },
      ].filter((one) => one.values.some((value) => value > 0));
      const siteLabel = (table.columns[label] ?? '').replace(/명$/, '');
      const only = series.length === 1 ? series[0]?.name : undefined;
      charts.push({
        ...(series.length > 1 ? { kind: 'stack' as const } : {}),
        // 경과 순으로 골랐으면 제목도 그 순위다(「예정일 경과 상위 4」).
        title: only === '예정일 경과'
          ? `${siteLabel}별 예정일 경과 ${status.attention}${ranked.length > limit ? ` 상위 ${limit}` : ''}`
          : ranked === overdue
            ? `${siteLabel}별 ${status.attention} 건수(예정일 경과 상위 ${top.length})`
            : `${siteLabel}별 ${status.attention} 건수${ranked.length > limit ? ` 상위 ${limit}` : ''}`,
        unit: '건', categories: top.map(([key]) => key), series,
        highlight: '예정일 경과',
      });
    } else if (top.length >= 2) {
      charts.push({
        title: `${table.columns[label]}별 ${status.attention} 건수${ranked.length > TOP ? ` 상위 ${TOP}` : ''}`,
        unit: '건', categories: top.map(([key]) => key),
        series: [{ name: status.attention, values: top.map(([, value]) => value) }], highlight: status.attention,
      });
    }
  }
  return charts;
}

/** 달 열 × 상태 — 달마다 완료·미실시 건수. 달이 셋 이상이면 선이 된다. */
function monthStatusChart(table: TableData): ReportChart | undefined {
  const status = statusColumn(table);
  if (!status) return undefined;
  const month = table.columns.findIndex((_, index) => index !== status.index && table.rows.every((row) => (
    /^(?:19|20)\d{2}-?(?:0[1-9]|1[0-2])$/.test(String(row[index] ?? '').trim()))));
  if (month < 0) return undefined;
  const months = Array.from(new Set(table.rows.map((row) => String(row[month]).trim()))).sort();
  if (months.length < 2 || months.length > FEW) return undefined;
  const names = Array.from(new Set(table.rows.map((row) => statusName(status, row[status.index])).filter(Boolean)))
    .sort((left, right) => (left === status.attention ? 1 : right === status.attention ? -1 : 0));
  return {
    title: `${table.columns[month]}별 처리 상태`, unit: '건', categories: months,
    series: names.map((name) => ({
      name,
      values: months.map((one) => table.rows.filter((row) => String(row[month]).trim() === one
        && statusName(status, row[status.index]) === name).length),
    })),
    highlight: status.attention,
  };
}

function tableCharts(table: TableData | undefined, basis?: Date, stackTop = STACK_TOP, lateFirst = false): ReportChart[] {
  if (!table || !table.rows.length) return [];
  // 상태 열이 있으면 언제나 상태로 — 달이 있으면 달별 상태를 앞에, 구성 막대는 그것과 겹치니 뺀다.
  const byStatus = statusCharts(table, basis, stackTop, lateFirst);
  if (byStatus) {
    const monthly = monthStatusChart(table);
    return monthly ? [monthly, ...byStatus.filter((chart) => chart.title !== '처리 상태')] : byStatus;
  }
  const rows = table.rows;
  const column = (index: number) => rows.map((row) => row[index]).filter(filled);
  const name = (index: number) => table.columns[index] ?? '항목';
  const numeric = (index: number) => {
    const cells = column(index);
    return cells.length > 0 && cells.filter((cell) => numberOf(cell) !== undefined).length >= cells.length * 0.8;
  };
  const dateish = (index: number) => {
    const cells = column(index);
    return NOT_MEASURE.test(name(index)) || (cells.length > 0 && cells.every((cell) => DATE_LIKE.test(String(cell).trim())));
  };
  const indexes = table.columns.map((_, index) => index);
  // 코드값 열은 더할 값이 아니다 — 결과 1·2 를 더해 「현장명별 결과 상위 10(값 200)」을 그렸다(실제 테넌트, 2026-10-23).
  const coded = (index: number) => {
    const cells = column(index).map((cell) => String(cell).trim());
    return CODE_NAME.test(name(index))
      || (!MEASURE_NAME.test(name(index)) && new Set(cells).size <= 6 && cells.every((cell) => /^\d$/.test(cell)));
  };
  const measure = indexes.find((index) => numeric(index) && !dateish(index) && !coded(index));
  // 묶을 열. 날짜·코드 이름의 열(점검일자)은 범주가 아니다 — 「월」처럼 달로 묶은 열만 범주로 쓴다.
  // 숫자 열이 있으면 이름마다 한 줄이어도 묶을 수 있고, 없으면 같은 값이 되풀이돼야 셀 것이 있다.
  const kinds = indexes
    .filter((index) => index !== measure && !NOT_CATEGORY.test(name(index))
      && !(numeric(index) && !column(index).every((cell) => DATE_LIKE.test(String(cell).trim()))))
    .map((index) => ({ index, unique: new Set(column(index).map((cell) => String(cell).trim())).size, cells: column(index).length }))
    .filter((one) => one.unique >= 2 && (measure !== undefined || one.unique < one.cells));
  const few = kinds.filter((one) => one.unique <= FEW);
  const many = kinds.filter((one) => one.unique > FEW
    && !column(one.index).every((cell) => FULL_DATE.test(String(cell).trim())));
  const charts: ReportChart[] = [];

  const ranked = (label: number, title: string): ReportChart | undefined => {
    const totals = new Map<string, number>();
    rows.forEach((row) => {
      if (!filled(row[label])) return;
      const key = String(row[label]).trim();
      totals.set(key, (totals.get(key) ?? 0) + (measure === undefined ? 1 : numberOf(row[measure]) ?? 0));
    });
    const keys = ordered(totals);
    // 달은 차례대로 최근 것을, 나머지는 많은 것을 TOP 개까지.
    const dated = DATE_LIKE.test(keys[0] ?? '');
    const top = dated ? keys.slice(-TOP) : keys.slice(0, TOP);
    const measureName = measure === undefined ? '건수' : name(measure);
    const cut = keys.length > top.length ? (dated ? ` 최근 ${TOP}` : ` 상위 ${TOP}`) : '';
    return top.length >= 2 ? {
      title: `${title}별 ${measureName}${cut}`,
      categories: top,
      series: [{ name: measureName, values: top.map((key) => totals.get(key) ?? 0) }],
      highlight: attentionOf([measureName]),
    } : undefined;
  };

  const [first, second] = few;
  if (measure === undefined && first && second) {
    // 두 범주를 엇갈려 센다 — 월 × 결과(완료·계획).
    const outer = new Map<string, number>();
    const inner = new Map<string, number>();
    const cell = new Map<string, number>();
    rows.forEach((row) => {
      if (!filled(row[first.index]) || !filled(row[second.index])) return;
      const a = String(row[first.index]).trim();
      const b = String(row[second.index]).trim();
      outer.set(a, (outer.get(a) ?? 0) + 1);
      inner.set(b, (inner.get(b) ?? 0) + 1);
      cell.set(`${a}\u0000${b}`, (cell.get(`${a}\u0000${b}`) ?? 0) + 1);
    });
    const categories = ordered(outer);
    const names = ordered(inner);
    charts.push({
      title: `${name(first.index)}별 ${name(second.index)} 건수`,
      categories,
      series: names.map((series) => ({ name: series, values: categories.map((a) => cell.get(`${a}\u0000${series}`) ?? 0) })),
      highlight: attentionOf(names),
    });
  } else {
    const label = first ?? many[0];
    const chart = label ? ranked(label.index, name(label.index)) : undefined;
    if (chart) charts.push(chart);
  }
  const named = many.find((one) => !charts.some((chart) => chart.title.indexOf(name(one.index)) === 0));
  const second_ = named ? ranked(named.index, name(named.index)) : undefined;
  if (second_) charts.push(second_);
  return charts;
}

function valuesOf(chart: ReportChart): number[] {
  return chart.series.reduce<(number | null)[]>((all, series) => all.concat(series.values), [])
    .filter((value): value is number => value !== null && value !== 0);
}

/** 달·날짜 범주. `9월`·`2026-09`·`2026년 9월`. */
function timeish(category: string): boolean {
  return DATE_LIKE.test(category) || /^\d{1,2}월$/.test(category) || /^(?:19|20)\d{2}년\s*\d{1,2}월$/.test(category);
}

/** 시간 흐름은 선으로 — 달이 둘이면 견주기라 막대로 둔다. */
/** 값의 단위 — 그림 제목에 「(단위: 건)」으로 붙는다. 모르면 붙이지 않는다. */
function unitOf(chart: ReportChart): string | undefined {
  const names = `${chart.title} ${chart.series.map((series) => series.name).join(' ')}`;
  if (/건수/.test(names)) return '건';
  if (/금액|매출|매입|수금|미수|원가|비용|잔액|단가/.test(names)) return '원';
  if (/인원|명수|사원\s*수/.test(names)) return '명';
  return undefined;
}

function shaped(chart: ReportChart): ReportChart {
  const unit = chart.unit ?? unitOf(chart);
  const withUnit = unit ? { ...chart, unit } : chart;
  if (chart.categories.length >= 3 && chart.categories.every(timeish)) return { ...withUnit, kind: 'line' };
  // 두세 칸이 모여 하나를 이루면(완료·미실시) 100% 누적 막대 하나 — 막대 둘은 지면 낭비다.
  // 「전체」「합계」가 든 수치 모음은 칸끼리 겹쳐 구성이 아니다.
  // 칸이 상태값(완료·미실시)일 때만 구성이다 — 9월·10월은 견주기, 현장 둘셋은 순위다.
  const parts = chart.series.length === 1 && chart.categories.length >= 2 && chart.categories.length <= 3
    && attentionOf(chart.categories) !== undefined
    && !chart.categories.some((category) => /전체|합계|총|평균/.test(category))
    && (chart.series[0]?.values ?? []).every((value) => value !== null && value >= 0);
  if (!parts) return withUnit;
  return { ...withUnit, kind: 'share', unit: '%', ...(chart.title === '주요 수치' ? { title: '처리 상태 구성' } : {}) };
}

/**
 * 〈표〉와 값이 같은 그림은 뺀다 — 경과 52건을 현장별 표와 막대로 똑같이 두 번 보여 줬다(국가직 팀장 3차 검토).
 * 표의 첫 열(이름)과 둘째 열(건수)이 그림의 범주·값과 모두 같으면 같은 그림이다.
 */
export function withoutTableTwins(
  charts: ReportChart[],
  exceptions?: { table: TableData },
  status?: TableData,
  /** 〈표 1〉 아래 줄에 쓴 수(경과·예정일 이내) — 완료·지연·기한 전 구성 그림은 〈표 1〉과 그 줄의 되풀이다(실제 테넌트, 2026-10-25). */
  stated: number[] = [],
): ReportChart[] {
  const counts = new Map((exceptions?.table.rows ?? []).map((row) => [String(row[0] ?? ''), Number(row[1])] as [string, number]));
  // 합계 요약표의 한 줄과 숫자가 같은 구성 막대도 같은 그림이다 — 〈표 1〉 1,910·438 과 겹쳐 한 장을 넘겼다.
  const statusRows = (status?.rows ?? []).map((row) => [...row.map((cell) => Number(cell)), ...stated]);
  return charts.filter((chart) => {
    // 달별 막대가 〈표 1〉 달별 줄과 값이 같으면 같은 그림이다 — 9·10월 두 달 판에서 〈그림 1〉이 〈표 1〉을 되풀이했다(2026-10-25).
    // 〈표 1〉 줄 이름의 시점(「'26.9월(마감)」)은 떼고 맞춘다 — 붙이자 같은 그림을 못 알아봤다.
    const monthly = chart.categories.length > 0 && chart.categories.every((category, index) => {
      const row = (status?.rows ?? []).find((one) => String(one[0]).replace(/\s*\(.*\)\s*$/, '') === category)
        ?.map((cell) => Number(cell));
      return !!row && chart.series.every((series) => series.values[index] === null
        || row.indexOf(series.values[index] as number) >= 0);
    });
    if (monthly) return false;
    if (chart.series.length !== 1) return true;
    const values = chart.series[0]?.values ?? [];
    if (counts.size && chart.categories.every((category, index) => counts.get(category) === values[index])) return false;
    // 0 칸(「기타 0」)은 보지 않는다 — 그것 때문에 〈표 1〉과 같은 구성 그림을 못 알아봤다(실제 테넌트, 2026-10-24).
    const shown = values.filter((value) => value !== 0);
    return !(chart.kind === 'share' && shown.length >= 2
      && statusRows.some((row) => shown.every((value) => value !== null && row.indexOf(value) >= 0)));
  });
}

/**
 * 보고서 그림. 여러 달을 조회한 판은 기준월 표로만 그린다 — 1턴 계산 요약은 여러 달을 합친 값이라 두 달 합계
 * 구성 그림이 남았다(2026-10-25 코드 리뷰).
 */
/**
 * 보고서 그림. crowded는 문서에 메모 확인 줄이 더 들어가는 판이다: 한 쪽이 넘치면 그림 범주부터 줄이고 메모 줄은
 * 남긴다(12차 검토, 메모 확인 줄이 들어간 판이 1,172px로 13.6% 넘쳤다).
 */
export function reportCharts(summary: unknown, table: TableData | undefined, basis: Date, multi: boolean, crowded = false): ReportChart[] {
  const top = multi ? MULTI_STACK_TOP : STACK_TOP;
  return chartsFor(multi ? undefined : summary, table, basis, crowded ? (multi ? top - 1 : top - 2) : top, crowded);
}

export function chartsFor(summary: unknown, table?: TableData, basis?: Date, stackTop = STACK_TOP, lateFirst = false): ReportChart[] {
  const head = summaryChart(summary);
  // 요약과 같은 숫자를 표로 또 세면 같은 그래프가 둘이다 — 완료 1,843·계획 506 을 두 번 그렸다(실제 테넌트, 2026-10-23).
  const repeats = (chart: ReportChart) => !!head && valuesOf(chart).every((value) => valuesOf(head).indexOf(value) >= 0);
  // 값이 모두 0 이면 그릴 것이 없다 — 「완료 0건·미완료 0건」 구성 막대가 나왔다(실제 테넌트, 2026-10-23).
  const drawn = (chart: ReportChart | undefined): chart is ReportChart => !!chart && valuesOf(chart).length > 0;
  return [head, ...tableCharts(table, basis, stackTop, lateFirst).filter((chart) => !repeats(chart))]
    .filter(drawn)
    .slice(0, MAX_CHARTS)
    .map(shaped);
}
